import liveServerStatus from '../../liveServerStatus';
import { format, addHours } from 'date-fns';

/**
 * Generates HTML email template for game testing reminders
 * @param {Object} employee - Employee information including firstName
 * @returns {string} - HTML formatted email
 */
const generateGameTestingReminderEmailHTML = (employee) => {
  // Get the deadline (24 hours from now)
  const deadline = addHours(new Date(), 24);
  const formattedDeadline = format(deadline, 'EEEE, MMMM do, yyyy, h:mm a');
  
  // Get first name for greeting
  const firstName = employee.firstName || employee.name?.split(' ')[0] || 'there';
  
  // Add test environment indicator in the header if not in live environment
  const testEnvironmentBanner = !liveServerStatus ? `
    <div style="background-color: #ffeb3b; color: #333; text-align: center; padding: 5px; font-weight: bold; font-size: 12px;">
      ⚠️ TEST EMAIL - No action required ⚠️
    </div>
  ` : '';

  return `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="UTF-8">
      <meta name="viewport" content="width=device-width, initial-scale=1.0">
      <title>Game Testing Reminder</title>
      <style>
        body {
          font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
          line-height: 1.6;
          color: #333;
          margin: 0;
          padding: 0;
          background-color: #f5f5f5;
        }
        .email-container {
          max-width: 600px;
          margin: 0 auto;
          background-color: #ffffff;
          border-radius: 8px;
          overflow: hidden;
          box-shadow: 0 3px 10px rgba(0, 0, 0, 0.1);
        }
        .email-header {
          background-color: #0077CC;
          color: white;
          padding: 20px;
          text-align: center;
        }
        .email-header h1 {
          margin: 0;
          font-size: 24px;
          font-weight: 600;
        }
        .email-body {
          padding: 30px;
        }
        .greeting {
          font-size: 18px;
          font-weight: 500;
          margin-bottom: 20px;
          color: #212121;
        }
        .testing-details {
          background-color: #f5f5f5;
          border-radius: 6px;
          padding: 20px;
          margin-bottom: 25px;
          border: 1px solid #e0e0e0;
        }
        .deadline-info {
          font-size: 16px;
          font-weight: 600;
          color: #0077CC;
          margin-bottom: 12px;
        }
        .testing-points {
          margin-top: 20px;
        }
        .testing-points ul {
          margin-top: 10px;
          padding-left: 20px;
        }
        .testing-points li {
          margin-bottom: 8px;
        }
        .action-needed {
          background-color: #e8f4fd;
          border-left: 4px solid #0077CC;
          padding: 12px 16px;
          margin: 20px 0;
          font-weight: 500;
        }
        .divider {
          height: 1px;
          background-color: #e0e0e0;
          margin: 25px 0;
        }
        .cta-button {
          display: inline-block;
          background-color: #0077CC;
          color: white;
          text-decoration: none;
          padding: 12px 25px;
          border-radius: 4px;
          font-weight: 500;
          margin-top: 5px;
          text-align: center;
        }
        .message {
          margin-bottom: 25px;
          color: #424242;
        }
        .email-footer {
          background-color: #212121;
          padding: 20px;
          text-align: center;
          color: #f5f5f5;
          font-size: 12px;
        }
        .company-name {
          font-weight: 600;
          color: #f5f5f5;
          margin-bottom: 8px;
        }
      </style>
    </head>
    <body>
      ${testEnvironmentBanner}
      <div class="email-container">
        <div class="email-header">
          <h1>Game Testing Reminder</h1>
        </div>
        
        <div class="email-body">
          <div class="greeting">
            Hello ${firstName},
          </div>
          
          <div class="message">
            This is a reminder that you have a pending game/app testing assignment. Your feedback is invaluable in helping us improve the quality of our games and applications before release.
          </div>
          
          <div class="testing-details">
            <div class="deadline-info">Deadline: ${formattedDeadline}</div>
            <div class="message">
              Please complete your testing and submit your report as soon as possible, preferably within the next 24 hours.
            </div>
            
            <div class="testing-points">
              <strong>Key points to remember:</strong>
              <ul>
                <li>Please test on your assigned platform/device</li>
                <li>Document any bugs or issues you encounter</li>
                <li>Provide feedback on user experience and gameplay</li>
                <li>Include screenshots where helpful</li>
              </ul>
            </div>
          </div>
          
          <div class="action-needed">
            Action required: Submit your testing report by ${formattedDeadline}.
          </div>
          
          <a style="color: white;" href="${process.env.REACT_APP_BASE_URL || 'https://ppc.myelinz.com'}" class="cta-button">Submit Testing Report</a>
          
          <div class="divider"></div>
          
          <div class="message">
            If you've already completed your testing, please disregard this message. Thank you for your contribution to improving our products.
          </div>
        </div>
        
        <div class="email-footer">
          <div class="company-name">MyelinZ PPC Portal</div>
          <p>This is an automated message. Please do not reply directly to this email.</p>
          <p>© ${new Date().getFullYear()} MyelinZ. All rights reserved.</p>
          ${!liveServerStatus ? '<p><strong>TEST ENVIRONMENT - No action required</strong></p>' : ''}
        </div>
      </div>
    </body>
    </html>
  `;
};

export default generateGameTestingReminderEmailHTML; 